import { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // בדיקה אם האפליקציה כבר מותקנת
    const checkIfInstalled = () => {
      if (window.matchMedia('(display-mode: standalone)').matches || 
          window.navigator.standalone === true) {
        setIsInstalled(true);
        return;
      }
    };

    // בדיקה אם זה iOS
    const checkIfIOS = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
      setIsIOS(isIOSDevice);
    };

    // האזנה לאירוע beforeinstallprompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // המתנה 3 שניות לפני הצגת ההודעה
      setTimeout(() => {
        const dismissed = localStorage.getItem('pwa-install-dismissed');
        const lastShown = localStorage.getItem('pwa-install-last-shown');
        const now = Date.now();
        
        // הצגה רק אם לא הושג לאחרונה או עברו 7 ימים
        if (!dismissed || (lastShown && now - parseInt(lastShown) > 7 * 24 * 60 * 60 * 1000)) {
          setShowPrompt(true);
        }
      }, 3000);
    };

    checkIfInstalled();
    checkIfIOS();

    if (!isInstalled) {
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isInstalled]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    try {
      const result = await deferredPrompt.prompt();
      console.log('PWA Install result:', result);
      
      if (result.outcome === 'accepted') {
        localStorage.setItem('pwa-installed', 'true');
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
    } catch (error) {
      console.error('Error during PWA installation:', error);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
    localStorage.setItem('pwa-install-last-shown', Date.now().toString());
  };

  const handleLater = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-last-shown', Date.now().toString());
  };

  // אם האפליקציה כבר מותקנת - לא להציג כלום
  if (isInstalled) return null;

  return (
    <AnimatePresence>
      {showPrompt && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className="fixed bottom-4 left-4 right-4 z-50 lg:bottom-8 lg:left-8 lg:right-auto lg:max-w-sm"
          dir="rtl"
        >
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-600 rounded-lg shadow-2xl backdrop-blur-sm">
            {/* כפתור סגירה */}
            <button
              onClick={handleDismiss}
              className="absolute top-2 left-2 text-gray-400 hover:text-white transition-colors p-1"
            >
              <X size={16} />
            </button>

            <div className="p-4">
              {/* Header */}
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-yellow-400 p-2 rounded-lg">
                  <Smartphone className="text-gray-900" size={20} />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">התקנת אפליקציה</h3>
                  <p className="text-gray-300 text-xs">SHA-SHA by Nataly</p>
                </div>
              </div>

              {/* Content */}
              <div className="mb-4">
                {isIOS ? (
                  <div className="text-gray-300 text-sm space-y-2">
                    <p className="font-medium">להוספה למסך הבית:</p>
                    <ol className="text-xs space-y-1 pr-4">
                      <li>• לחץ על כפתור השיתוף בדפדפן</li>
                                             <li>• בחר &quot;הוסף למסך הבית&quot;</li>
                      <li>• אשר את ההוספה</li>
                    </ol>
                  </div>
                ) : (
                  <p className="text-gray-300 text-sm">
                    קבל גישה מהירה לחנות תכשיטי SHA-SHA ישירות מהמסך הראשי של הטלפון!
                  </p>
                )}
              </div>

              {/* Benefits */}
              <div className="mb-4">
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
                  <div className="flex items-center gap-1">
                    <span className="w-1 h-1 bg-yellow-400 rounded-full"></span>
                    טעינה מהירה יותר
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-1 h-1 bg-yellow-400 rounded-full"></span>
                    עבודה ללא אינטרנט
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-1 h-1 bg-yellow-400 rounded-full"></span>
                    הודעות על מבצעים
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-1 h-1 bg-yellow-400 rounded-full"></span>
                    חוויית אפליקציה
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                {!isIOS && (
                  <button
                    onClick={handleInstallClick}
                    className="flex-1 bg-yellow-400 text-gray-900 px-4 py-2 rounded-lg font-medium text-sm hover:bg-yellow-300 transition-colors flex items-center justify-center gap-2"
                  >
                    <Download size={16} />
                    התקן עכשיו
                  </button>
                )}
                <button
                  onClick={handleLater}
                  className="px-4 py-2 text-gray-300 hover:text-white transition-colors text-sm"
                >
                  מאוחר יותר
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PWAInstallPrompt; 